import {
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IUploadImportFileDto } from 'common/src/types/importFile.dtos';
import { IImportFileTag, IPage } from 'common/src/types/interfaces';

import { QUERY_KEYS } from 'portal/src/api/constants';
import {
  updateImportFile,
  uploadImportFiles,
} from 'portal/src/api/ImportFileService';
import { getImportFileTags } from 'portal/src/api/ImportFileTagService';
import useAuth from 'portal/src/auth/useAuth';
import FileUploader from 'portal/src/components/billAutomation/forms/FileUploader';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import FormController from '../common/forms/FormController';
import { selectInputType } from '../common/forms/FormFieldTypes';
import UploadHistoryTable from './UploadHistoryTable/UploadHistoryTable';

type TagFormInput = Pick<IUploadImportFileDto, 'tagText'>;

const SpreadsheetUpload = () => {
  const queryClient = useQueryClient();
  const snackbarHandler = useSnackbarHandler();
  const { hasSystemAdminAccess } = useAuth();
  const isSysAdmin = hasSystemAdminAccess();

  const {
    data: tags,
    isLoading: isLoadingTags,
    isFetching: isFetchingTags,
  } = useQuery({
    queryKey: [QUERY_KEYS.tags],
    queryFn: async () => await getImportFileTags(),
    initialData: { data: [] } as IPage<IImportFileTag>,
  });

  const saveTagMutation = useMutation({
    mutationFn: async ({
      rowId,
      tagValue,
    }: {
      rowId: string;
      tagValue: string;
    }) => {
      await updateImportFile(rowId, { tags: tagValue });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.importFiles] });
      snackbarHandler.success('Tag updated successfully.');
    },
    onError: () => {
      snackbarHandler.error(
        'An error occurred while trying to update the tag.',
      );
    },
  });

  const tagFieldOptions = tags.data.map((tag) => ({
    label: `${tag.displayText} - ${tag.text}`,
    value: tag.text,
  }));

  const { control, handleSubmit, reset } = useForm({
    values: { tagText: tags.data[0]?.text || '' },
  });

  const tagField: selectInputType = {
    placeholder: 'File Tag',
    label: 'File Tag',
    name: 'tagText',
    type: 'selectInput',
    required: true,
    values: tagFieldOptions,
    helperText: '(Uploaded files will be processed based on the selected tag.)',
  };

  const handleUpload = async (
    files: File[],
    { tagText }: TagFormInput,
  ): Promise<void> => {
    await uploadImportFiles({
      files,
      tagText,
    } as unknown as IUploadImportFileDto);
    reset();
  };

  if (isLoadingTags || isFetchingTags) {
    return (
      <>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton
            variant="rectangular"
            width="70%"
            height={200}
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width="30%"
            height={100}
            sx={{ borderRadius: 2 }}
          />
        </Stack>
        <Stack direction="column" spacing={1} alignItems="center" mt={5}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={30}
            />
          ))}
        </Stack>
      </>
    );
  }

  return (
    <Stack marginInline={2} pt={3}>
      {tags.data.length > 0 ? (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <FileUploader<TagFormInput>
              fileTypeCategory="table"
              uploadHandlers={{
                uploadFiles: async (files, data) => handleUpload(files, data),
                handleSubmit,
              }}
              refetchKey="importFiles"
              isDisabled={saveTagMutation.isPending}
              sidePanel={
                <Container
                  maxWidth="xs"
                  sx={{
                    backgroundColor: 'background.paper',
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'grey.300',
                  }}
                >
                  <Typography variant="body1" gutterBottom>
                    Select a tag to submit with your upload(s):
                  </Typography>
                  <Grid container>
                    <FormController
                      control={control}
                      fieldConf={{
                        ...tagField,
                        disabled: saveTagMutation.isPending,
                      }}
                    />
                  </Grid>
                </Container>
              }
            />
          </Stack>
          <Divider sx={{ mt: 4, mb: 2 }}>
            Spreadsheet Upload & Automation History
          </Divider>
          <UploadHistoryTable
            contextName={NESTED_ROUTES.BILL_AUTOMATION.SPREADSHEET_UPLOAD}
            isSysAdmin={isSysAdmin}
            loading={saveTagMutation.isPending}
            tagConfigs={{
              tagFieldOptions,
              isSavingTag: saveTagMutation.isPending,
              onSaveTag: async ({ rowId, tagValue }) => {
                await saveTagMutation.mutateAsync({ rowId, tagValue });
              },
            }}
            variant="spreadsheet"
          />
        </>
      ) : (
        <Stack>
          <Typography marginTop={4}>
            Please email <u>support@nimble-energy.com</u> to enable spreadsheet
            uploads for your organization.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default SpreadsheetUpload;
